import { useEffect } from 'react'
import { X } from 'lucide-react'
import Sparkline from './Sparkline'

interface DailySeries {
  date: string
  index: number
}

interface Contributor {
  name: string
  value: number | string
}

interface MetricDrawerProps {
  open: boolean
  onClose: () => void
  title: string
  value: number
  series?: DailySeries[]
  seriesMode?: 'line' | 'bar'
  seriesColor?: string
  contributors?: Contributor[]
}

export default function MetricDrawer({
  open,
  onClose,
  title,
  value,
  series,
  seriesMode = 'line',
  seriesColor = 'currentColor',
  contributors,
}: MetricDrawerProps) {
  // Escape to close
  useEffect(() => {
    if (!open) return
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [open, onClose])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[150]" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="absolute top-0 right-0 h-full w-[380px] max-w-[92vw] bg-card border-l border-border shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="h-14 px-5 flex items-center justify-between border-b border-border shrink-0">
          <span className="text-sm font-semibold text-foreground truncate">{title}</span>
          <button
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition duration-150 shrink-0 ml-2"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5">
          {/* Value headline */}
          <div className="text-3xl font-bold tabular-nums text-foreground">{value}</div>

          {/* Sparkline (large) */}
          {series && series.length > 0 && (
            <div className="rounded-lg bg-muted/40 px-3 py-2">
              <Sparkline points={series} mode={seriesMode} color={seriesColor} height={64} />
            </div>
          )}

          {/* Contributors list */}
          {contributors && contributors.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground mb-2">
                Top contributors
              </p>
              <div className="flex flex-col gap-1">
                {contributors.map((c, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between px-3 py-2 rounded-md bg-muted/30 border border-border"
                  >
                    <span className="text-sm text-foreground truncate">{c.name}</span>
                    <span className="text-sm font-medium tabular-nums text-muted-foreground shrink-0 ml-2">
                      {c.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
